import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UserContext, UserPayload } from '../../Context/userContext';
import { IThreePsCodeItem } from './threePsCodes.model';
import {
    buildSavePayload,
    getChangedRows,
    hasUnsavedChanges as checkHasUnsavedChanges,
    hasValidationErrors,
} from './threePsCodes.utils';
import { useThreePsCodesStore } from './store/threePsCodes.store';
import { selectIsEditMode, selectSelectedVendor, selectSelectedVendorCodes } from './store/threePsSelectors';
import { DEFAULT_PAGE_INDEX, THREE_PS_DATA_UID, UNSAVED_CHANGES_CONFIRMATION_MESSAGE } from './threePsCodes.constants';

export const useThreePsCodesPage = () => {
    const { setShowLoader } = useContext(UserContext) as UserPayload;

    const codesByVendor = useThreePsCodesStore(selectSelectedVendorCodes);
    const isEditMode = useThreePsCodesStore(selectIsEditMode);
    const selectedVendor = useThreePsCodesStore(selectSelectedVendor);

    const fetchVendors = useThreePsCodesStore((state) => state.fetchVendors);
    const saveThreePsCode = useThreePsCodesStore((state) => state.saveThreePsCode);
    const setIsEditMode = useThreePsCodesStore((state) => state.setIsEditMode);
    const fetchCodesByVendor = useThreePsCodesStore((state) => state.fetchCodesByVendor);
    const setPageSize = useThreePsCodesStore((state) => state.setPageSize);
    const setPageIndex = useThreePsCodesStore((state) => state.setPageIndex);

    const [editableCodes, setEditableCodes] = useState<IThreePsCodeItem[]>([]);
    const [originalCodes, setOriginalCodes] = useState<IThreePsCodeItem[]>([]);
    const [attemptedSave, setAttemptedSave] = useState(false);

    useEffect(() => {
        const load = async () => {
          await fetchVendors(setShowLoader);
          const { selectedVendor: vendor } = useThreePsCodesStore.getState();
          if (vendor) {
            await fetchCodesByVendor(vendor, { setShowLoader });
          }
        };
        void load();
      }, [fetchVendors, fetchCodesByVendor, setShowLoader]);

    useEffect(() => {
        setEditableCodes(codesByVendor);
        setOriginalCodes(codesByVendor);
        setAttemptedSave(false);
        setIsEditMode(false);
    }, [codesByVendor, setIsEditMode]);

    const handleCancel = useCallback(() => {
        setEditableCodes(originalCodes);
        setAttemptedSave(false);
    }, [originalCodes]);

    const handleFieldChange = useCallback(
        (id: string, field: keyof IThreePsCodeItem, value: string | boolean | null) => {
            setEditableCodes((prev) =>
                prev.map((row) =>
                    row[THREE_PS_DATA_UID] === id ? { ...row, [field]: value } : row
                )
            );
        },
        []
    );

    const hasUnsavedChanges = useMemo(
        () => checkHasUnsavedChanges(editableCodes, originalCodes),
        [editableCodes, originalCodes]
    );

    const confirmChange = useCallback(() => {
        if (!isEditMode || !hasUnsavedChanges) return true;
        return globalThis.confirm(UNSAVED_CHANGES_CONFIRMATION_MESSAGE);
    }, [hasUnsavedChanges, isEditMode]);

    const handleSave = useCallback(async () => {
        setAttemptedSave(true);
        if (hasValidationErrors(editableCodes)) return false;

        const changedRows = getChangedRows(editableCodes, originalCodes);
        if (changedRows.length === 0) return true;

        setShowLoader(true);
        try {
            await saveThreePsCode(buildSavePayload(changedRows));
            const saveError = useThreePsCodesStore.getState().saveError;
            return !saveError;
        } finally {
            setShowLoader(false);
        }
    }, [editableCodes, originalCodes, saveThreePsCode, setShowLoader]);

    const handleChangePage = useCallback(async (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        if (!selectedVendor) return;
        if (!confirmChange()) return;
        const nextPageIndex = newPage + 1;
        setPageIndex(nextPageIndex);
        await fetchCodesByVendor(selectedVendor, {
            setShowLoader,
            force: true,
            pageIndex: nextPageIndex,
        });
    }, [selectedVendor, fetchCodesByVendor, setShowLoader, setPageIndex, confirmChange]);

    const handleChangePageSize = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!selectedVendor) return;
        if (!confirmChange()) return;
        const nextPageSize = Number(event.target.value);
        setPageSize(nextPageSize);
        setPageIndex(DEFAULT_PAGE_INDEX);
        await fetchCodesByVendor(selectedVendor, {
            setShowLoader,
            force: true,
            pageIndex: DEFAULT_PAGE_INDEX,
            pageSize: nextPageSize,
        });
    }, [selectedVendor, fetchCodesByVendor, setPageSize, setPageIndex, setShowLoader, confirmChange]);

    return {
        editableCodes,
        attemptedSave,
        confirmChange,
        handleCancel,
        handleFieldChange,
        handleSave,
        handleChangePage,
        handleChangePageSize,
    };
};
